import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Apple, Download, Play, Smartphone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PortalShell } from "@/components/AppHeader";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { API, api, errMsg } from "@/lib/api";

const ORIGIN = API.replace(/\/api$/, "");

const Qr = ({ value, testId }) => (
  <div data-testid={testId} className="rounded-xl bg-white p-2.5"><QRCodeSVG value={value} size={112} bgColor="#ffffff" fgColor="#0a0a0c" /></div>
);

const Option = ({ icon: Icon, title, sub, url, onClick, busy, testId, qr }) => (
  <div data-testid={`app-option-${testId}`} className="lux-card flex min-w-0 flex-col gap-4 p-5 sm:p-6">
    <div className="flex items-center gap-3"><Icon className="h-6 w-6 text-amber-400" /><div><p className="text-lg text-amber-50">{title}</p><p className="text-xs text-zinc-500">{sub}</p></div></div>
    {url || onClick ? (
      <div className="flex flex-wrap items-end justify-between gap-4">
        {url ? <a href={url} target="_blank" rel="noreferrer" data-testid={`app-button-${testId}`} className="btn-gold">Download</a>
          : <button data-testid={`app-button-${testId}`} onClick={onClick} disabled={busy} className="btn-gold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download APK</button>}
        {qr && <div className="hidden sm:block"><Qr value={qr} testId={`app-qr-${testId}`} /></div>}
      </div>
    ) : <p data-testid={`app-coming-soon-${testId}`} className="rounded-full border border-white/10 px-4 py-2 text-center text-sm text-zinc-400">Coming soon</p>}
  </div>
);

const Downloads = ({ d }) => {
  const [apkUrl, setApkUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const fetchLink = useCallback(async () => {
    const { data } = await api.post("/app/apk-link");
    const u = `${ORIGIN}${data.path}`;
    setApkUrl(u);
    return u;
  }, []);
  useEffect(() => { if (d.apk.available) fetchLink().catch(() => {}); }, [d.apk.available, fetchLink]);
  const download = async () => {
    setBusy(true);
    try { window.location.href = await fetchLink(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const noStores = !d.ios.url && !d.android.url;
  return (
    <div className="mt-8 space-y-8">
      {d.version_label && <p data-testid="app-version" className="text-sm text-zinc-400">Current version <span className="text-amber-200">{d.version_label}</span></p>}
      <div className="grid gap-4 md:grid-cols-3">
        <Option icon={Apple} title="iPhone" sub="App Store" url={d.ios.url} qr={d.ios.url} testId="ios" />
        <Option icon={Play} title="Android" sub="Google Play" url={d.android.url} qr={d.android.url} testId="android" />
        <Option icon={Smartphone} title="Android (direct)" sub="APK file · allow installs from your browser" onClick={d.apk.available ? download : null} busy={busy} qr={apkUrl} testId="apk" />
      </div>
      {d.apk.available && <p className="text-xs text-zinc-500">The APK link and QR code are personal and expire after 15 minutes — reload this page for a fresh one.</p>}
      {noStores && (
        <div data-testid="app-early-access" className="lux-card grid gap-6 border-amber-400/30 p-5 sm:grid-cols-[1fr_auto] sm:items-center sm:p-6">
          <div>
            <p className="lux-eyebrow">Early access</p>
            <h2 className="mt-2 text-xl text-amber-50">Try the preview app today</h2>
            <p className="mt-2 text-sm text-zinc-400">Install the free <span className="text-amber-200">Expo Go</span> app from the App Store or Google Play, then scan this code or open the link on your phone. {d.early_access.note}</p>
            <a href={d.early_access.url} target="_blank" rel="noreferrer" data-testid="app-early-access-link" className="mt-4 inline-block break-all text-sm text-amber-300 underline underline-offset-4">{d.early_access.url}</a>
          </div>
          <Qr value={d.early_access.url} testId="app-early-access-qr" />
        </div>
      )}
    </div>
  );
};

const Visitor = () => (
  <div className="min-h-screen bg-[#0a0a0c]">
    <header className="mx-auto flex h-20 max-w-7xl items-center px-5 sm:h-24 sm:px-10"><Logo /></header>
    <main className="mx-auto max-w-3xl px-5 py-10 sm:px-10" data-testid="get-app-visitor">
      <p className="lux-eyebrow">Phone app</p>
      <h1 className="mt-3 text-4xl font-semibold text-amber-50 sm:text-5xl">Get the App</h1>
      <p className="mt-4 text-zinc-400">The Luxury Rideshare app is available to members. Sign in or join to download it — riders right after sign-up, drivers once approved.</p>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/login" data-testid="get-app-signin" className="btn-gold">Sign in</Link>
        <Link to="/signup" data-testid="get-app-join-rider" className="btn-outline-gold">Join as a rider</Link>
        <Link to="/signup?role=driver" data-testid="get-app-join-driver" className="btn-outline-gold">Apply to drive</Link>
      </div>
    </main>
  </div>
);

export default function GetTheApp() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { if (user && user.role !== "admin") api.get("/app/downloads").then(({ data }) => setD(data)).catch(() => {}); }, [user]);
  if (!user) return <Visitor />;
  return (
    <PortalShell>
      <div data-testid="get-app-page">
        <p className="lux-eyebrow">Phone app</p>
        <h1 className="mt-2 text-3xl font-semibold text-amber-50 sm:text-4xl">Get the App</h1>
        {user.role === "admin" && <p className="mt-4 text-sm text-zinc-400">Manage download links in Admin → Mobile app.</p>}
        {d && !d.eligible && <p data-testid="get-app-pending" className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-200">{d.message}</p>}
        {d?.eligible && <Downloads d={d} />}
      </div>
    </PortalShell>
  );
}
